import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, CloudUpload } from 'lucide-react';
import { formatINR } from '@vj/shared';
import { api } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';

const STATUS_TONE = {
  issued: 'text-success',
  draft: 'text-muted',
  cancelled: 'text-danger',
};

const PAYMENT_TONE = {
  paid: 'text-success',
  partial: 'text-warning',
  unpaid: 'text-danger',
  refund_due: 'text-info',
};

const PAYMENT_LABEL = {
  paid: 'Paid',
  partial: 'Partial',
  unpaid: 'Unpaid',
  refund_due: 'Refund due',
};

const COLUMNS = [
  { key: 'invoice_no', header: 'Invoice #', sortKey: 'invoice_no', render: (r) => r.invoice_no ?? <span className="italic text-muted">Draft</span> },
  {
    key: 'invoice_date',
    header: 'Date',
    sortKey: 'invoice_date',
    render: (r) => new Date(r.invoice_date).toLocaleDateString('en-IN'),
  },
  { key: 'customer_name', header: 'Customer' },
  {
    key: 'grand_total',
    header: 'Total',
    align: 'right',
    sortKey: 'grand_total',
    render: (r) => formatINR(r.grand_total),
  },
  {
    key: 'payment_status',
    header: 'Payment',
    render: (r) => (
      <span className={PAYMENT_TONE[r.payment_status]}>{PAYMENT_LABEL[r.payment_status] ?? 'Unpaid'}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => <span className={STATUS_TONE[r.status]}>{r.status === 'issued' ? 'Issued' : r.status === 'draft' ? 'Draft' : 'Cancelled'}</span>,
  },
];

export default function InvoicesListPage() {
  const [query, onQueryChange] = useListQuery({ sort: 'invoice_date' });
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const navigate = useNavigate();
  const location = useLocation();

  const listQuery = useQuery({
    queryKey: ['invoices', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/invoices', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Sales &amp; GST invoices</h1>
        <Button onClick={() => navigate('/invoices/new')}>
          <Plus size={17} aria-hidden="true" /> New invoice
        </Button>
      </div>

      {location.state?.savedOffline ? (
        <p className="flex items-center gap-2 rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          <CloudUpload size={16} aria-hidden="true" /> Saved on this device — it&rsquo;ll upload automatically once you&rsquo;re back online.
        </p>
      ) : null}

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        searchPlaceholder="Search by invoice number, customer or mobile…"
        emptyLabel="No invoices yet. Create the first one."
      />
    </div>
  );
}
